// Which support conversations this admin has actually read.
//
// The API's `unreadForAdmin` means "still waiting on a reply" — it clears when
// one is sent, not when the thread is opened, and there is no admin-side read
// endpoint to call. So opening a conversation cleared nothing: the next poll
// brought the flag straight back and the dot and the bell badge returned a few
// seconds later.
//
// Reading is a per-person, per-browser fact rather than a property of the
// conversation, so it lives in localStorage. What's stored is the timestamp of
// the last message seen, not a boolean: a NEW reply into a thread that was
// already read is unread again, which a flag could not express.
//
// (If the API ever grows an admin read endpoint, call it from `markSeen` and
// this becomes a cache rather than the source of truth.)

const KEY = 'agrobridge-admin-support-seen';

// Same-tab listeners; `storage` only fires in OTHER tabs, and the bell needs
// to hear the Support page next to it.
const CHANGED = 'agrobridge:support-seen';

function readAll() {
    try {
        return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch {
        return {};
    }
}

const time = (at) => (at ? Date.parse(at) || 0 : 0);

/**
 * Has this conversation said something since it was last read?
 *
 * `waiting` is the server's own flag: a thread nobody is waiting on is never
 * "unread" here, however long ago it was looked at.
 */
export function hasUnseen(id, lastAt, waiting = true) {
    if (!waiting) return false;
    const seenAt = readAll()[id];
    if (!seenAt) return true;
    // No timestamp on the last message: having opened it once is all we can go on.
    if (!lastAt) return false;
    return time(lastAt) > time(seenAt);
}

/** Remember that everything up to `lastAt` in this conversation has been read. */
export function markSeen(id, lastAt) {
    if (!id) return;
    const stamp = lastAt || new Date().toISOString();
    const all = readAll();
    if (all[id] === stamp) return;
    all[id] = stamp;
    try {
        localStorage.setItem(KEY, JSON.stringify(all));
    } catch {
        // Private mode, or storage full. The badge stays until the reply goes
        // out, which is the old behaviour rather than a broken one.
    }
    window.dispatchEvent(new Event(CHANGED));
}

/** Fires when anything in here is marked read, so the bell can catch up. */
export function onSeenChange(handler) {
    window.addEventListener(CHANGED, handler);
    return () => window.removeEventListener(CHANGED, handler);
}
